// src/components/Financial/InvoiceBoletosTable.js

import React, { useEffect, useState, useCallback } from 'react'
import {
    Card,
    CardHeader,
    CardContent,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Tooltip,
    CircularProgress,
    Box,
    Typography,
    Chip,
    Link,
    Stack,
} from '@mui/material'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import RefreshIcon from '@mui/icons-material/Refresh'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'

import { api } from '../../../../../services/Main/Api'
import { Subtitles } from '@mui/icons-material'

const formatCurrency = (value) => {
    if (value == null) return 'R$ 0,00'
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
    }).format(Number(value))
}

const formatDate = (value) => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    })
}

const getStatusColor = (status) => {
    switch (status) {
        case 'Criado':
        case 'created':
        case 'CREATED':
            return 'info'

        case 'Pode ser registrado':
        case 'can_be_registered':
        case 'CAN_BE_REGISTERED':
            return 'primary'

        case 'Sendo registrado':
        case 'registering':
        case 'REGISTERING':
            return 'warning'

        case 'Registrado':
        case 'registered':
        case 'REGISTERED':
            return 'info'

        case 'Rejeitado':
        case 'rejected':
        case 'REJECTED':
            return 'error'

        case 'Pago':
        case 'paid':
        case 'PAID':
        case 'SETTLED':
        case 'settled':
            return 'success'

        case 'Cancelado':
        case 'canceled':
        case 'CANCELED':
            return 'error'

        case 'Erro interno':
        case 'INTERNAL_ERROR':
        case 'internal_error':
            return 'error'

        case 'Erro de saldo':
        case 'BALANCE_ERROR':
        case 'balance_error':
            return 'error'

        default:
            return 'default'
    }
}

/**
 * Componente de tabela de boletos de um Invoice.
 *
 * Props:
 *  - invoiceId (number | string): ID do invoice que terá os boletos listados
 */
const InvoiceBoletosTable = ({ invoiceId, invoice }) => {
    const [boletos, setBoletos] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const fetchBoletos = useCallback(async () => {
        if (!invoiceId) return

        setLoading(true)
        setError(null)

        try {
            const { data } = await api.get('bank-slip/', {
                params: { invoice_id: invoiceId },
            })
            setBoletos(Array.isArray(data) ? data : data.results || [])
        } catch (err) {
            console.error('Erro ao carregar boletos do invoice', err)
            setError('Não foi possível carregar os boletos. Tente novamente.')
        } finally {
            setLoading(false)
        }
    }, [invoiceId])

    useEffect(() => {
        fetchBoletos()
    }, [fetchBoletos])


    const handleCancelBoleto = async (boletoId) => {
        try {
            await api.patch(`bank-slip/${boletoId}/`)
            fetchBoletos() // atualizar tabela
        } catch (err) {
            console.error("Erro ao cancelar boleto:", err)
        }
    }


    if (!invoiceId) {
        return (
            <Card>
                <CardHeader title="Boletos do Invoice" />
                <CardContent>
                    <Typography variant="body2" color="text.secondary">
                        Nenhum invoice selecionado. Informe um <strong>invoiceId</strong> para listar os boletos.
                    </Typography>
                </CardContent>
            </Card>
        )
    }

    console.log(invoice?.status?.key)

    return (
        <Card sx={{ marginTop: '1rem', marginBottom: '1rem' }}>
            <CardHeader
                avatar={<Subtitles sx={{ fontSize: 32 }} />}
                title="Boletos"
            />
            <CardContent>
                {loading && (
                    <Box
                        sx={{
                            py: 4,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                {!loading && error && (
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                {!loading && !error && boletos.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        Nenhum boleto gerado para este invoice.
                    </Typography>
                )}

                {!loading && !error && boletos.length > 0 && (

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Nº do Documento</TableCell>
                                <TableCell>Vencimento</TableCell>
                                <TableCell>Valor</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {boletos.map((boleto) => (
                                <TableRow key={boleto.id}>
                                    <TableCell>BOL-{boleto.id}</TableCell>
                                    <TableCell>{boleto.document_number || '-'}</TableCell>
                                    <TableCell>{formatDate(boleto.due_date)}</TableCell>
                                    <TableCell>{formatCurrency(boleto.total_value)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={boleto.status || '—'}
                                            size="small"
                                            color={getStatusColor(boleto.status)}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                            {/* Abrir PDF */}
                                            {boleto.file && (
                                                <Tooltip title="Abrir boleto em PDF">
                                                    <IconButton
                                                        size="small"
                                                        component={Link}
                                                        href={boleto.file}
                                                        target="_blank"
                                                        rel="noopener"
                                                    >
                                                        <OpenInNewRoundedIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {/* Cancelar boleto — se não estiver pago ou cancelado */}
                                            {(!['Pago', 'Cancelado'].includes(boleto.status) && invoice?.status?.key === "paid") && (
                                                <Tooltip title="Cancelar boleto">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleCancelBoleto(boleto.id)}
                                                        sx={{ color: 'error.main' }}
                                                    >
                                                        <HighlightOffIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Stack>
                                    </TableCell>

                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    )
}

export default InvoiceBoletosTable
